/**
 * Bundle Builder
 * Handles Foxsell Mix & Match bundle functionality
 */
class BundleBuilder {
  constructor() {
    this.config = window.foxsell?.bundleConfig;
    if (!this.config) {
      console.warn('Bundle configuration not found');
      return;
    }

    this.categorySelections = new Map();
    this.addonSelections = [];
    this.currentModal = null;
    this.shareTab = 'reddit';

    // Assembly is a real Shopify product/variant (nominated in section settings),
    // not a synthetic number, so it can be added to the cart as a genuine line
    // item and priced from its own catalog price.
    this.readAssemblyConfig();
    this.storageKey = `bundle-builder:${this.config.id}`;

    this.init();
  }

  init() {
    this.initializeDefaultSelections();
    this.bindEvents();
    this.applySharedSelectionsFromUrl();
    this.maybeOfferSavedBuild();

    // Small delay to ensure DOM is fully ready
    setTimeout(() => {
      this.updateSummary();
      this.updateProgress();
    }, 100);
  }

  /**
   * Pre-select the first item in each category
   */
  initializeDefaultSelections() {
    const selections = document.querySelectorAll('[data-category-selection]');
    
    selections.forEach(selection => {
      const categoryId = selection.dataset.categorySelection;
      const categoryTitle = selection.dataset.categoryTitle;
      const productId = selection.dataset.productId;
      const variantId = selection.dataset.variantId;
      const price = selection.dataset.price;

      // Every product in this category is sold out (or the category has no
      // products), so the server didn't render a product/variant/price -
      // skip it rather than seeding categorySelections with an unparsable
      // price, which turned into "NaN" once it hit computeTotals().
      if (!productId || !variantId || !price) return;

      const titleEl = selection.querySelector('.bundle-selected__title');
      const title = titleEl ? titleEl.textContent : 'Unknown Product';
      const variantSelect = selection.querySelector('.bundle-selected__variant-select');
      const variantTitle = variantSelect ? variantSelect.options[variantSelect.selectedIndex].text : '';

      this.categorySelections.set(categoryId, {
        productId,
        variantId,
        categoryId,
        categoryTitle,
        type: 'product',
        title,
        variantTitle,
        price: this.formatPrice(price),
        quantity: 1
      });
    });
  }

  /**
   * Rebuild categorySelections/addonSelections directly from the DOM's
   * authoritative data-* attributes right before an add-to-cart submission.
   * This is deliberately NOT just "trust whatever is already in memory" -
   * someone can poke at window.bundleBuilder's in-memory state (or a
   * displayed price string) from the console, but every row's real
   * product/variant/price still lives in data attributes set by our own
   * server-rendered markup and selection flow, so re-deriving from those
   * immediately before checkout closes that gap.
   */
  resyncSelectionsFromDom() {
    document.querySelectorAll('[data-category-selection]').forEach((selection) => {
      const categoryId = selection.dataset.categorySelection;
      const productId = selection.dataset.productId;
      const variantId = selection.dataset.variantId;
      const price = selection.dataset.price;
      if (!productId || !variantId || !price) return;

      const existing = this.categorySelections.get(categoryId);
      const variantSelect = selection.querySelector('.bundle-selected__variant-select');
      const variantTitle = variantSelect ? variantSelect.options[variantSelect.selectedIndex].text : '';

      this.categorySelections.set(categoryId, {
        productId,
        variantId,
        categoryId,
        categoryTitle: selection.dataset.categoryTitle || existing?.categoryTitle || '',
        type: 'product',
        title: selection.dataset.productTitle || existing?.title || 'Unknown Product',
        variantTitle,
        price: this.formatPrice(price),
        quantity: existing?.quantity || 1
      });
    });

    const selectedAddonButtons = document.querySelectorAll('.bundle-addon-card__add.button--secondary');
    this.addonSelections = Array.from(selectedAddonButtons)
      .filter((button) => button.dataset.productId && button.dataset.variantId && button.dataset.price)
      .map((button) => ({
        variantId: button.dataset.variantId,
        productId: button.dataset.productId,
        type: 'addOns',
        title: button.dataset.title || 'Add-on',
        variantTitle: '',
        price: this.formatPrice(button.dataset.price),
        quantity: 1
      }));
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Use event delegation for dynamically added elements
    document.addEventListener('click', (e) => {
      // Change buttons
      if (e.target.closest('.bundle-category__change')) {
        this.openCategoryModal(e);
      }
      
      // Modal close buttons and overlay
      if (e.target.closest('.bundle-category-modal__close') || e.target.classList.contains('bundle-category-modal__overlay')) {
        this.closeCategoryModal(e);
      }
      
      // Product selection in modal
      if (e.target.closest('.bundle-modal-product-card__select')) {
        this.handleProductSelection(e);
      }
      
      // Addon add buttons
      if (e.target.closest('.bundle-addon-card__add')) {
        this.handleAddAddon(e);
      }
      
      // Add to cart button
      if (e.target.closest('[data-bundle-add-to-cart]')) {
        e.preventDefault();
        this.handleAddToCart();
      }

      // Save build
      if (e.target.closest('[data-bundle-save]')) {
        this.saveBuild();
      }

      // Open share modal
      if (e.target.closest('[data-bundle-share]')) {
        this.openShareModal();
      }

      // Close share modal
      if (e.target.closest('[data-bundle-share-close]')) {
        this.closeShareModal();
      }

      // Switch share format tab
      const tabButton = e.target.closest('[data-share-tab]');
      if (tabButton) {
        this.setShareTab(tabButton.dataset.shareTab);
      }

      // Copy share text
      if (e.target.closest('[data-bundle-share-copy]')) {
        this.copyShareField('[data-bundle-share-text]');
      }

      // Copy share link
      if (e.target.closest('[data-bundle-share-copy-link]')) {
        this.copyShareField('[data-bundle-share-link]');
      }
    });

    // Variant selectors for selected products
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('bundle-selected__variant-select')) {
        this.handleVariantChange(e);
      }
    });

    // Escape key to close modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.currentModal) this.closeCategoryModal();
        this.closeShareModal();
      }
    });
  }

  /**
   * Open category selection modal
   */
  openCategoryModal(e) {
    e.preventDefault();
    const button = e.target.closest('.bundle-category__change');
    if (!button) return;
    
    const categoryId = button.dataset.categoryId;
    const modal = document.querySelector(`[data-category-modal="${categoryId}"]`);
    
    if (modal) {
      modal.hidden = false;
      // Force layout before adding the class so the slide-in transition runs
      // eslint-disable-next-line no-unused-expressions
      modal.offsetHeight;
      modal.classList.add('on');
      this.currentModal = modal;
      document.body.style.overflow = 'hidden';
    } else {
      console.error('Modal not found for category:', categoryId);
    }
  }

  /**
   * Close the currently open modal (slides out, then hides after the
   * transition so the drawer isn't yanked off-screen instantly)
   */
  closeCategoryModal(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (this.currentModal) {
      const modal = this.currentModal;
      modal.classList.remove('on');
      document.body.style.overflow = '';
      setTimeout(() => {
        modal.hidden = true;
      }, 300);
      this.currentModal = null;
    }
  }

  /**
   * Handle product selection from modal
   */
  handleProductSelection(e) {
    const button = e.target.closest('.bundle-modal-product-card__select');
    if (!button) return;
    
    // Don't allow interaction with disabled buttons (sold out products)
    if (button.disabled) return;
    
    const categoryId = button.dataset.categoryId;
    const categoryTitle = button.dataset.categoryTitle;
    const productId = button.dataset.productId;
    const productTitle = button.dataset.productTitle;
    const productImage = button.dataset.productImage;
    const compareAt = button.dataset.compareAt ? parseFloat(button.dataset.compareAt) : null;

    let variantsObj;
    try {
      variantsObj = JSON.parse(button.dataset.variants);
    } catch (error) {
      console.error('Error parsing variants:', error);
      return;
    }

    // Convert variants object to array of [id, price] pairs
    const variantsArray = Object.entries(variantsObj);

    if (variantsArray.length === 0) {
      console.error('No variants found for product:', productId);
      return;
    }

    // Get first variant
    const [firstVariantId, firstVariantPrice] = variantsArray[0];

    // Update the selected product display. The row markup is:
    // .bundle-category (row) > .rthumb (image), .rmain[data-category-selection] (title), .rfoot (price)
    const selectionContainer = document.querySelector(`[data-category-selection="${categoryId}"]`);
    const row = selectionContainer ? selectionContainer.closest('.bundle-category') : null;

    if (selectionContainer && row) {
      // Update image (lives in the sibling .rthumb, not inside selectionContainer)
      const imageContainer = row.querySelector('.rthumb');
      if (imageContainer && productImage) {
        imageContainer.innerHTML = `
          <div class="bundle-selected__image">
            <img
              src="${productImage}"
              alt="${this.escapeHtml(productTitle)}"
              class="bundle-selected__img thumb"
              loading="lazy"
            >
          </div>
        `;
      }

      // Update title
      let titleEl = selectionContainer.querySelector('.bundle-selected__title');
      if (!titleEl) {
        titleEl = document.createElement('div');
        titleEl.className = 'rprod bundle-selected__title';
        selectionContainer.querySelector('.rcat').insertAdjacentElement('afterend', titleEl);
      }
      titleEl.innerHTML = this.escapeHtml(productTitle);

      // Update or create variant selector
      let variantSelect = selectionContainer.querySelector('.bundle-selected__variant-select');

      if (variantsArray.length > 1) {
        if (!variantSelect) {
          // Create variant selector if it doesn't exist
          variantSelect = document.createElement('select');
          variantSelect.className = 'bundle-selected__variant-select';
          variantSelect.dataset.productId = productId;
          variantSelect.dataset.categoryId = categoryId;
          selectionContainer.appendChild(variantSelect);

          // Add change listener
          variantSelect.addEventListener('change', (e) => this.handleVariantChange(e));
        }

        variantSelect.innerHTML = variantsArray.map(([variantId, variantPrice]) => {
          return `<option value="${variantId}" data-price="${variantPrice}">${variantId}</option>`;
        }).join('');
      } else if (variantSelect) {
        // Remove variant selector if only one variant
        variantSelect.remove();
      }

      // Update price + retail/save (lives in the sibling .rfoot)
      this.renderRowPrice(row, firstVariantPrice, compareAt);

      // Update data attributes
      selectionContainer.dataset.productId = productId;
      selectionContainer.dataset.productTitle = productTitle;
      selectionContainer.dataset.variantId = firstVariantId;
      selectionContainer.dataset.price = firstVariantPrice;
      selectionContainer.dataset.compareAt = compareAt || '';

      // Force a repaint by adding/removing a class
      selectionContainer.classList.add('bundle-category__selected--updating');
      requestAnimationFrame(() => {
        selectionContainer.classList.remove('bundle-category__selected--updating');
      });
    }

    // Update category selection in memory
    this.categorySelections.set(categoryId, {
      productId,
      variantId: firstVariantId,
      categoryId,
      categoryTitle,
      type: 'product',
      title: productTitle,
      variantTitle: '',
      price: this.formatPrice(firstVariantPrice),
      quantity: 1
    });

    // Refresh the "Chosen" state within this modal's grid so it stays correct
    // if the shopper reopens it without a full page reload
    const modal = button.closest('.bundle-category-modal');
    if (modal) {
      modal.querySelectorAll('.bundle-modal-product-card').forEach((card) => {
        const cardButton = card.querySelector('.bundle-modal-product-card__select');
        const isCurrent = cardButton === button;
        card.classList.toggle('bundle-modal-product-card--current', isCurrent);
        cardButton.classList.toggle('bundle-modal-product-card__select--current', isCurrent);
        if (!cardButton.disabled) {
          cardButton.textContent = isCurrent ? 'Chosen' : 'Choose';
        }
      });
    }

    // Close modal
    this.closeCategoryModal();

    // Update summary
    this.updateSummary();
  }

  /**
   * Handle variant change for selected products
   */
  handleVariantChange(e) {
    const select = e.target;
    if (!select.classList.contains('bundle-selected__variant-select')) return;
    
    const categoryId = select.dataset.categoryId;
    const selectedOption = select.options[select.selectedIndex];
    const variantId = selectedOption.value;
    const price = selectedOption.dataset.price;
    const compareAt = selectedOption.dataset.compareAt ? parseFloat(selectedOption.dataset.compareAt) : null;

    const selection = this.categorySelections.get(categoryId);
    if (selection) {
      selection.variantId = variantId;
      selection.variantTitle = selectedOption.text;
      selection.price = this.formatPrice(price);

      // Update price display
      const container = select.closest('[data-category-selection]');
      const row = container.closest('.bundle-category');
      if (row) {
        this.renderRowPrice(row, price, compareAt);
      }
      container.dataset.price = price;
      container.dataset.compareAt = compareAt || '';

      this.updateSummary();
    }
  }

  /**
   * Render the price + retail/save markup in a row's .rfoot, matching the
   * Mwave concept's rprice-v / rprice-rrp / rprice-save treatment
   */
  renderRowPrice(row, price, compareAt) {
    const priceContainer = row.querySelector('.rprice');
    if (!priceContainer) return;

    const priceNum = typeof price === 'string' ? parseFloat(price) : price;
    // Sanity cap: ignore compare-at prices more than 3x the selling price,
    // which reads as broken seed/merchandising data rather than a real sale
    const compareAtValid = compareAt && compareAt > priceNum && compareAt <= priceNum * 3;
    const save = compareAtValid ? compareAt - priceNum : 0;

    priceContainer.innerHTML = `
      <span class="rprice-v" data-product-price>${this.formatPrice(priceNum)}</span>
      ${save > 0 ? `
        <span class="rprice-rrp" data-product-compare-at>${this.formatPrice(compareAt)}</span>
        <span class="rprice-save" data-product-save>Save ${this.formatPrice(save)}</span>
      ` : ''}
    `;
  }

  /**
   * Handle adding/removing an addon product
   */
  handleAddAddon(e) {
    const button = e.target.closest('.bundle-addon-card__add');
    if (!button) return;
    
    // Don't allow interaction with disabled buttons (sold out products)
    if (button.disabled) return;
    
    const card = button.closest('.bundle-addon-row');
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    
    // Check if this addon is already added
    const existingIndex = this.addonSelections.findIndex(
      addon => addon.productId === productId && addon.variantId === variantId
    );
    
    if (existingIndex !== -1) {
      // Remove addon
      this.addonSelections.splice(existingIndex, 1);
      button.textContent = button.dataset.addText || 'Add to Bundle';
      button.classList.remove('button--secondary');
      button.classList.add('button--primary');
      button.disabled = false;
      
      // Re-enable all other addon buttons
      this.updateAddonButtonStates();
    } else {
      // Check if we've reached the maximum
      const maxAddons = this.getMaxAddons();

      if (this.addonSelections.length >= maxAddons) {
        const message = button.dataset.maxMessage || `You can only add up to ${maxAddons} add-ons to your bundle.`;
        alert(message);
        return;
      }
      
      // Add addon
      const title = card.querySelector('.rcat').textContent;
      const priceElement = card.querySelector('.rprice-v');
      const price = priceElement ? priceElement.textContent : '';

      this.addonSelections.push({
        variantId,
        productId,
        type: 'addOns',
        title,
        variantTitle: '',
        price,
        quantity: 1
      });
      
      button.textContent = button.dataset.removeText || 'Remove';
      button.classList.remove('button--primary');
      button.classList.add('button--secondary');
      
      // Update button states based on limit
      this.updateAddonButtonStates();
    }

    this.updateSummary();
  }

  /**
   * Remove an addon from selections (called from summary)
   */
  removeAddon(productId, variantId) {
    const index = this.addonSelections.findIndex(
      addon => addon.productId === productId && addon.variantId === variantId
    );
    
    if (index !== -1) {
      this.addonSelections.splice(index, 1);
      
      // Update the button state in the addon card
      const button = document.querySelector(
        `.bundle-addon-card__add[data-product-id="${productId}"][data-variant-id="${variantId}"]`
      );
      if (button) {
        button.textContent = button.dataset.addText || 'Add to Bundle';
        button.classList.remove('button--secondary');
        button.classList.add('button--primary');
        button.disabled = false;
      }
      
      // Re-enable all addon buttons
      this.updateAddonButtonStates();
      this.updateSummary();
    }
  }

  /**
   * The configured add-on maximum, capped to how many add-ons actually exist
   * on the page - showing "0 / 5 selected" when only 3 add-ons are visible
   * doesn't make sense, so the smaller number always wins.
   */
  getMaxAddons() {
    const configured = this.config.settings?.add_on?.maximum;
    const availableCount = document.querySelectorAll('.bundle-addon-card__add').length;
    if (!configured) return availableCount || Infinity;
    return Math.min(configured, availableCount || configured);
  }

  /**
   * Update addon button states based on maximum limit
   */
  updateAddonButtonStates() {
    const maxAddons = this.getMaxAddons();

    // The forced assembly item rides in the same __foxsell:addOns slot as
    // the optional add-ons a shopper picks here (Foxsell only allows
    // `maximum` addOns-type entries in total, and doesn't distinguish
    // "forced" from "optional"). If assembly is still being charged
    // (threshold not met), it's already occupying one of those slots, so
    // it counts toward the limit even before the shopper touches an
    // optional add-on button - otherwise picking one more than Foxsell
    // allows silently breaks the whole bundle (stuck at $0 in the cart).
    const { assemblyFee } = this.computeTotals();
    const consumedSlots = this.addonSelections.length + (assemblyFee > 0 ? 1 : 0);
    const atLimit = consumedSlots >= maxAddons;
    
    // Update count display
    const countElement = document.querySelector('[data-addon-count]');
    if (countElement) {
      countElement.textContent = this.addonSelections.length;
    }
    
    // Get all addon buttons
    const addonButtons = document.querySelectorAll('.bundle-addon-card__add');
    
    addonButtons.forEach(button => {
      const productId = button.dataset.productId;
      const variantId = button.dataset.variantId;
      
      // Check if this addon is currently selected
      const isSelected = this.addonSelections.some(
        addon => addon.productId === productId && addon.variantId === variantId
      );
      
      // Check if button is sold out (has "Sold out" text)
      const isSoldOut = button.textContent.trim().toLowerCase().includes('sold out');
      
      // Don't modify disabled state for sold out products
      if (isSoldOut) {
        return;
      }
      
      // Disable unselected buttons if at limit
      if (!isSelected && atLimit) {
        button.disabled = true;
      } else if (!isSelected) {
        button.disabled = false;
      }
    });
  }

  /**
   * Format price as currency
   */
  formatPrice(price) {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numPrice); // Foxsell prices are already in dollars, not cents
  }

  /**
   * Update the bundle summary display
   */
  updateSummary() {
    const itemsContainer = document.querySelector('[data-bundle-items]');
    const addToCartBtn = document.querySelector('[data-bundle-add-to-cart]');

    if (!itemsContainer) {
      return;
    }

    const allItems = [
      ...Array.from(this.categorySelections.values()),
      ...this.addonSelections
    ];

    if (allItems.length === 0) {
      itemsContainer.innerHTML = '<p class="bundle-summary__empty">No items selected</p>';
      if (addToCartBtn) addToCartBtn.disabled = true;
      return;
    }

    const categoryItems = Array.from(this.categorySelections.values());
    const addonItems = this.addonSelections;

    let html = '';
    
    // Category items (cannot be removed)
    categoryItems.forEach(item => {
      const escapedTitle = this.escapeHtml(item.title);
      const escapedVariant = item.variantTitle ? this.escapeHtml(item.variantTitle) : '';
      
      html += `
        <div class="bundle-summary__item">
          <div class="bundle-summary__item-info">
            <div class="bundle-summary__item-title">${escapedTitle}</div>
            ${escapedVariant ? `<div class="bundle-summary__item-variant">${escapedVariant}</div>` : ''}
          </div>
          <span class="bundle-summary__item-price">${item.price}</span>
        </div>
      `;
    });

    // Addon items (can be removed)
    addonItems.forEach((item) => {
      const escapedTitle = this.escapeHtml(item.title);
      const escapedVariant = item.variantTitle ? this.escapeHtml(item.variantTitle) : '';
      
      html += `
        <div class="bundle-summary__item">
          <div class="bundle-summary__item-info">
            <div class="bundle-summary__item-title">${escapedTitle}</div>
            ${escapedVariant ? `<div class="bundle-summary__item-variant">${escapedVariant}</div>` : ''}
          </div>
          <span class="bundle-summary__item-price">${item.price}</span>
          <button 
            type="button" 
            class="bundle-summary__item-remove"
            data-product-id="${item.productId}"
            data-variant-id="${item.variantId}"
            aria-label="Remove ${escapedTitle}"
          >
            ×
          </button>
        </div>
      `;
    });

    itemsContainer.innerHTML = html;
    
    // Bind remove buttons
    itemsContainer.querySelectorAll('.bundle-summary__item-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const button = e.target.closest('.bundle-summary__item-remove');
        const productId = button.dataset.productId;
        const variantId = button.dataset.variantId;
        this.removeAddon(productId, variantId);
      });
    });

    const { partsTotal, addonsTotal, discountPct, discountAmount, assemblyFee, grandTotal } =
      this.computeTotals();

    const totalElement = document.querySelector('[data-bundle-total]');
    if (totalElement) {
      totalElement.textContent = this.formatPrice(grandTotal);
    }

    this.renderBreakdown(partsTotal, addonsTotal, discountPct, discountAmount, assemblyFee);
    this.updateAssemblyPromo(partsTotal + addonsTotal);

    // Swapping a category selection can cross the free-assembly threshold,
    // which changes whether assembly is occupying an addOns slot - refresh
    // the optional add-on buttons' disabled state every time, not just on
    // add-on clicks.
    this.updateAddonButtonStates();

    // A category where every product is sold out has no valid selection to
    // add to cart (see initializeDefaultSelections / resyncSelectionsFromDom,
    // which both skip it rather than fabricate a broken price) - block
    // checkout entirely rather than silently submitting an incomplete bundle.
    const hasUnavailableCategory = document.querySelector('[data-category-unavailable="true"]') !== null;
    const unavailableWarning = document.querySelector('[data-bundle-unavailable-warning]');
    if (unavailableWarning) {
      unavailableWarning.hidden = !hasUnavailableCategory;
    }

    if (addToCartBtn) {
      addToCartBtn.disabled = hasUnavailableCategory;
    }

    this.updateProgress();
  }

  /**
   * Read the nominated assembly product/variant + free-assembly threshold
   * straight from the server-rendered [data-assembly-promo] element. Called
   * again (not just cached from the constructor) immediately before an
   * add-to-cart submission, so a shopper poking at in-memory JS state can't
   * change the fee without also editing the source data attributes.
   */
  readAssemblyConfig() {
    const promoEl = document.querySelector('[data-assembly-promo]');
    this.assemblyProductId = promoEl?.dataset.assemblyProductId || null;
    this.assemblyVariantId = promoEl?.dataset.assemblyVariantId || null;
    this.assemblyTitle = promoEl?.dataset.assemblyTitle || 'Assembly & testing';
    this.assemblyFee = promoEl ? parseFloat(promoEl.dataset.assemblyPrice) || 0 : 0;
    this.assemblyThreshold = promoEl ? parseFloat(promoEl.dataset.assemblyThreshold) || 0 : 0;
  }

  /**
   * Foxsell's own "dynamic_pricing" strategy: settings.price.value is a
   * discount percentage applied across the whole bundle (parts + add-ons),
   * per https://partner-docs.foxsell.app/data-structures/mix-and-match-bundles.md.
   * Foxsell's checkout already charges this discounted amount - we just
   * weren't reflecting it on-page, so the displayed total didn't match the
   * cart total.
   */
  getBundleDiscountPct() {
    const price = this.config.settings?.price;
    if (price?.strategy === 'dynamic_pricing' && price.value) {
      const pct = parseFloat(price.value);
      return Number.isFinite(pct) ? pct : 0;
    }
    return 0;
  }

  /**
   * Parts / add-ons / bundle discount / assembly fee, matching the Mwave
   * concept's partsTotal() / servicesTotal() / assemblyFee() split
   */
  computeTotals() {
    const parsePrice = (item) => parseFloat(item.price.replace(/[^0-9.-]+/g, ''));
    const toCents = (dollars) => Math.round(dollars * 100);

    const partsPricesCents = Array.from(this.categorySelections.values()).map((item) =>
      toCents(parsePrice(item))
    );
    const partsTotalCents = partsPricesCents.reduce((sum, c) => sum + c, 0);
    const partsTotal = partsTotalCents / 100;

    const addonsTotal = this.addonSelections.reduce((sum, item) => sum + parsePrice(item), 0);

    // Foxsell's "dynamic_pricing" discount only applies to items typed
    // "product" (the core parts) - items typed "addOns" (our own add-ons,
    // and the assembly line) are charged at full price. It also discounts
    // PER LINE ITEM (rounding each item's discounted price to the cent),
    // not the aggregate parts sum - confirmed against real cart/add.js +
    // cart.js round trips where aggregate rounding was occasionally 1c off.
    const discountPct = this.getBundleDiscountPct();
    const discountedPartsCents = partsPricesCents.reduce(
      (sum, cents) => sum + Math.round(cents * (1 - discountPct / 100)),
      0
    );
    const discountedParts = discountedPartsCents / 100;
    const discountAmount = partsTotal - discountedParts;

    const assemblyFee = this.getAssemblyFee(discountedParts + addonsTotal);
    // grandTotal is what the shopper actually pays - shown on-page. rawTotal
    // is the pre-discount sum of every item (parts + add-ons + assembly)
    // sent as __foxsell:original_bundle_price; Foxsell applies its own
    // configured discount (to the "product"-typed portion) from that
    // "original" figure, so sending an already-discounted number there
    // would double-discount.
    const grandTotal = discountedParts + addonsTotal + assemblyFee;
    const rawTotal = partsTotal + addonsTotal + assemblyFee;

    return { partsTotal, addonsTotal, discountPct, discountAmount, assemblyFee, grandTotal, rawTotal };
  }

  /**
   * Real assembly product's own price, waived (fee = 0, and therefore left
   * out of the cart entirely) once parts + add-ons clear the free threshold
   */
  getAssemblyFee(subtotal) {
    if (!this.assemblyVariantId || !this.assemblyFee) return 0;
    if (this.assemblyThreshold && subtotal >= this.assemblyThreshold) return 0;
    return this.assemblyFee;
  }

  /**
   * Render the Parts / Add-Ons / Bundle discount / Assembly rows above the total
   */
  renderBreakdown(partsTotal, addonsTotal, discountPct, discountAmount, assemblyFee) {
    const partsEl = document.querySelector('[data-parts-total]');
    if (partsEl) partsEl.textContent = this.formatPrice(partsTotal);

    const addonsRow = document.querySelector('[data-addons-row]');
    const addonsEl = document.querySelector('[data-addons-total]');
    if (addonsRow && addonsEl) {
      addonsRow.hidden = addonsTotal <= 0;
      addonsEl.textContent = this.formatPrice(addonsTotal);
    }

    // Foxsell's own bundle-wide "dynamic_pricing" discount, so the displayed
    // total matches what the cart actually charges
    const discountRow = document.querySelector('[data-discount-row]');
    const discountEl = document.querySelector('[data-discount-total]');
    if (discountRow && discountEl) {
      discountRow.hidden = discountAmount <= 0;
      discountEl.textContent = `-${this.formatPrice(discountAmount)}`;
      const labelEl = discountRow.querySelector('[data-discount-label]');
      if (labelEl) labelEl.textContent = `Bundle discount (${discountPct}% off)`;
    }

    const isWaived = this.assemblyFee > 0 && assemblyFee === 0;
    const strikePrice = `<span class="bundle-summary__row-was">${this.formatPrice(this.assemblyFee)}</span>`;

    // The summary panel has no separate status badge, so it needs the "Free"
    // text alongside the struck-through price to make sense on its own.
    const assemblyEl = document.querySelector('[data-assembly-total]');
    if (assemblyEl) {
      assemblyEl.innerHTML = isWaived
        ? `${strikePrice}<span class="bundle-summary__row-free">Free</span>`
        : this.formatPrice(assemblyFee);
    }

    // The forced assembly line in the main list already has a status badge
    // (Included / Free), so the price column just shows the struck-through
    // amount without repeating "Free" a second time.
    const lineEl = document.querySelector('[data-assembly-line]');
    const linePriceEl = document.querySelector('[data-assembly-line-price]');
    if (lineEl && linePriceEl) {
      linePriceEl.innerHTML = isWaived ? strikePrice : this.formatPrice(assemblyFee);
      const badgeEl = lineEl.querySelector('[data-assembly-line-badge]');
      if (badgeEl) badgeEl.textContent = isWaived ? 'Free' : 'Included';
    }
  }

  /**
   * Update the "$X to go" promo bar toward free assembly
   */
  updateAssemblyPromo(subtotal) {
    const textEl = document.querySelector('[data-assembly-promo-text]');
    const fillEl = document.querySelector('[data-assembly-promo-fill]');
    if (!textEl || !fillEl || !this.assemblyThreshold) return;

    const pct = Math.min(100, Math.round((subtotal / this.assemblyThreshold) * 100));
    fillEl.style.width = `${pct}%`;

    if (subtotal >= this.assemblyThreshold) {
      textEl.textContent = 'Free assembly unlocked';
    } else {
      const toGo = this.assemblyThreshold - subtotal;
      textEl.textContent = `${this.formatPrice(toGo)} to go for free assembly`;
    }
  }

  /**
   * Update the fill on the summary progress bar (categories filled / total)
   */
  updateProgress() {
    const fillEl = document.querySelector('[data-bundle-progress-fill]');
    if (!fillEl) return;

    const totalCategories = this.config.categories?.length || 0;
    if (totalCategories === 0) {
      fillEl.style.width = '0%';
      return;
    }

    const filled = this.categorySelections.size;
    const pct = Math.min(100, Math.round((filled / totalCategories) * 100));
    fillEl.style.width = `${pct}%`;
  }

  /**
   * Add bundle to cart via Shopify Cart API
   */
  async handleAddToCart() {
    // Belt-and-braces: refuse to submit even if the button's disabled state
    // was bypassed (e.g. via devtools) while a category has no in-stock
    // product to fulfil it.
    if (document.querySelector('[data-category-unavailable="true"]')) return;

    const addToCartBtn = document.querySelector('[data-bundle-add-to-cart]');

    // Store original button text and disable
    const originalText = addToCartBtn ? addToCartBtn.textContent : '';
    const loadingText = addToCartBtn?.dataset.loadingText || 'Adding...';
    
    if (addToCartBtn) {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = loadingText;
    }
    
    const currentDate = Date.now();
    const identifier = `${this.config.id}_${currentDate}`;

    // Re-derive everything from the DOM's data-* attributes right before
    // submitting, rather than trusting in-memory state or displayed text
    // someone may have edited via devtools.
    this.readAssemblyConfig();
    this.resyncSelectionsFromDom();

    const allItems = [
      ...Array.from(this.categorySelections.values()),
      ...this.addonSelections
    ];

    // Foxsell's real embed never sends a "category" key on "addOns"-type
    // entries (confirmed against a captured working cart/add.js payload) -
    // only "product"-type entries carry it. Always including it (even as
    // an empty string for add-ons) is a schema mismatch that makes
    // Foxsell's server-side cart transform silently fail to expand the
    // bundle, leaving only the empty placeholder line item in the cart.
    const bundleItems = allItems.map(item => {
      const bundleItem = {
        variantId: parseInt(item.variantId),
        quantity: item.quantity,
        type: item.type,
        properties: {}
      };
      if (item.type === 'product') {
        bundleItem.category = item.categoryTitle || '';
      }
      return bundleItem;
    });

    const { assemblyFee, rawTotal } = this.computeTotals();

    // The assembly product goes into dynamic_add_on_bundle_items as type
    // "addOns" (Foxsell's own recognised type for add-ons, confirmed against
    // a real working Foxsell embed payload) so its price is included in
    // Foxsell's server-side total - NOT discounted like "product" items, but
    // genuinely charged. An earlier attempt tagged it with an invented
    // "assembly" type, which Foxsell silently ignores (drops from pricing
    // entirely), and a later attempt added it as a second cart line, which
    // a shopper could just remove from the cart before checkout. Neither is
    // right - it must ride inside the same bundle line's properties.
    if (assemblyFee > 0 && this.assemblyVariantId) {
      bundleItems.push({
        variantId: parseInt(this.assemblyVariantId),
        quantity: 1,
        type: 'addOns',
        properties: {}
      });
    }

    // original_bundle_price is the pre-discount sum of every item (parts +
    // add-ons + assembly) - Foxsell applies its own configured discount to
    // the "product"-typed portion server-side from this figure.
    const totalPrice = Math.round(rawTotal * 100);

    // Build properties object with individual product titles and Foxsell data.
    // Keys must be the plain product title with NO underscore prefix - a
    // captured working Foxsell embed payload confirms this. Foxsell's
    // server-side cart transform appears to correlate these properties by
    // exact title to expand the bundle; the underscore-prefixed form used
    // here previously (on the assumption it just hid the property from the
    // cart/checkout display) silently broke that correlation, so the
    // bundle line item was added with no expanded children.
    const properties = {};

    allItems.forEach(item => {
      properties[item.title] = `x${item.quantity}`;
    });

    if (assemblyFee > 0 && this.assemblyVariantId) {
      properties[this.assemblyTitle] = 'x1';
    }

    // Add Foxsell required properties
    properties['__foxsell:original_bundle_price'] = Math.round(totalPrice);
    properties['__foxsell:dynamic_add_on_bundle_id'] = identifier;
    properties['__foxsell:dynamic_add_on_bundle_items'] = JSON.stringify(bundleItems);

    const formData = {
      items: [{
        id: this.config.bundleVariantId,
        quantity: 1,
        properties: properties
      }],
      sections: ['cart-drawer', 'cart-icon-bubble']
    };

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Cart API error:', errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();
      
      // Show success state
      const successText = addToCartBtn?.dataset.successText || 'Added to cart';
      if (addToCartBtn) {
        addToCartBtn.textContent = successText;
      }
      
      // Dispatch cart update event using theme's event system
      // The cart drawer and other components listen for 'cart:update' events
      const cartEvent = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'bundle-builder',
          data: {
            source: 'bundle-builder',
            itemCount: 1,
            sections: response.sections
          }
        }
      });
      
      document.dispatchEvent(cartEvent);
      
      // Reset addons only (keep category selections)
      this.addonSelections = [];
      this.updateSummary();
      
      // Restore button after 1 second
      setTimeout(() => {
        if (addToCartBtn) {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = originalText;
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      alert('Failed to add bundle to cart. Please try again.');
      
      // Restore button state on error
      if (addToCartBtn) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = originalText;
      }
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Serialize the current build to a plain object: one variant id per
   * category, plus a list of selected add-on variant ids
   */
  getShareableState() {
    const categories = {};
    this.categorySelections.forEach((selection, categoryId) => {
      categories[categoryId] = { productId: selection.productId, variantId: selection.variantId };
    });

    const addons = this.addonSelections.map((item) => ({
      productId: item.productId,
      variantId: item.variantId
    }));

    return { categories, addons };
  }

  /**
   * Find the "Choose"/"Change" button for a product already rendered in a
   * category's modal and click through the normal selection flow. Every
   * category renders its full product list up front, so no extra product
   * lookup/fetch is needed to restore a saved or shared build.
   */
  selectProductInCategory(categoryId, productId) {
    const button = document.querySelector(
      `.bundle-modal-product-card__select[data-category-id="${categoryId}"][data-product-id="${productId}"]`
    );
    if (!button || button.disabled) return false;
    this.handleProductSelection({ target: button });
    return true;
  }

  /**
   * Click an add-on's "Add to Bundle" button if it isn't already selected
   */
  selectAddon(productId) {
    const button = document.querySelector(`.bundle-addon-card__add[data-product-id="${productId}"]`);
    if (!button || button.disabled) return false;
    const alreadySelected = this.addonSelections.some((item) => item.productId === productId);
    if (alreadySelected) return false;
    this.handleAddAddon({ target: button });
    return true;
  }

  /**
   * Apply a serialized state (from localStorage or a shared URL) on top of
   * the page's default selections
   */
  applySharedState(state) {
    if (!state) return;

    Object.entries(state.categories || {}).forEach(([categoryId, selection]) => {
      const current = this.categorySelections.get(categoryId);
      if (current && current.productId === String(selection.productId)) return;
      this.selectProductInCategory(categoryId, selection.productId);
    });

    (state.addons || []).forEach((addon) => this.selectAddon(addon.productId));

    this.updateSummary();
  }

  /**
   * ?bb_build=<encoded state> in the URL restores a shared build. Mirrors
   * the concept's ?build=<id> contract, but encodes the actual selection
   * since Foxsell bundles have no server-side preset ids.
   */
  applySharedSelectionsFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get('bb_build');
    if (!encoded) return;

    try {
      const state = JSON.parse(atob(decodeURIComponent(encoded)));
      this.applySharedState(state);
    } catch (error) {
      console.warn('Could not parse shared build link:', error);
    }
  }

  /**
   * Save the current build to this browser so it can be picked up again
   * later without an account (Foxsell has no server-side save endpoint)
   */
  saveBuild() {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(this.getShareableState()));
      this.toast('Build saved to this browser');
    } catch (error) {
      console.warn('Could not save build:', error);
    }
  }

  /**
   * On load, silently restore a build saved earlier in this browser
   */
  maybeOfferSavedBuild() {
    if (new URLSearchParams(window.location.search).get('bb_build')) return; // shared link takes priority

    let saved;
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) return;
      saved = JSON.parse(raw);
    } catch (error) {
      return;
    }

    this.applySharedState(saved);
    this.toast('Your saved build has been restored');
  }

  /**
   * Build the copy-paste summary text for the share modal, matching the
   * three formats mwave.com.au's own configurator generates (Reddit
   * markdown, Whirlpool BBCode, plain text) via its GetContent() endpoint
   */
  buildShareText(format) {
    const { grandTotal, assemblyFee } = this.computeTotals();
    const categoryItems = Array.from(this.categorySelections.values());
    const addonItems = this.addonSelections;
    const lines = [];

    if (format === 'reddit') {
      lines.push('**Mwave.com.au Custom Build PC**', '');
      categoryItems.forEach((item) => lines.push(`- **${item.title}** — ${item.price}`));
      addonItems.forEach((item) => lines.push(`- ${item.title} — ${item.price}`));
      if (assemblyFee > 0) lines.push('', `**Assembly Fee:** **${this.formatPrice(assemblyFee)}**`);
      lines.push('', `**Total: ${this.formatPrice(grandTotal)}**`, `*Generated by Mwave.com.au ${new Date().toLocaleDateString()}*`);
    } else if (format === 'whirlpool') {
      lines.push('[b]Mwave.com.au Custom Build PC[/b]', '');
      categoryItems.forEach((item) => lines.push(`[*]${item.title} — ${item.price}[/*]`));
      addonItems.forEach((item) => lines.push(`[*]${item.title} — ${item.price}[/*]`));
      if (assemblyFee > 0) lines.push('', `[*]Assembly Fee: ${this.formatPrice(assemblyFee)}[/*]`);
      lines.push('', `[b]Total: ${this.formatPrice(grandTotal)}[/b]`, `[i]Generated by Mwave.com.au ${new Date().toLocaleDateString()}[/i]`);
    } else {
      lines.push('Mwave.com.au Custom Build PC', '');
      categoryItems.forEach((item) => lines.push(`${item.title} — ${item.price}`));
      addonItems.forEach((item) => lines.push(`${item.title} — ${item.price}`));
      if (assemblyFee > 0) lines.push('', `Assembly Fee: ${this.formatPrice(assemblyFee)}`);
      lines.push('', `Total: ${this.formatPrice(grandTotal)}`, `Generated by Mwave.com.au ${new Date().toLocaleDateString()}`);
    }

    return lines.join('\n');
  }

  /**
   * Current page URL with the build state encoded, so it reproduces the
   * exact same selections when opened elsewhere
   */
  getShareUrl() {
    const encoded = encodeURIComponent(btoa(JSON.stringify(this.getShareableState())));
    const url = new URL(window.location.href);
    url.searchParams.set('bb_build', encoded);
    return url.toString();
  }

  openShareModal() {
    const modal = document.querySelector('[data-bundle-share-modal]');
    if (!modal) return;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    const linkInput = modal.querySelector('[data-bundle-share-link]');
    if (linkInput) linkInput.value = this.getShareUrl();

    this.setShareTab(this.shareTab);
  }

  closeShareModal() {
    const modal = document.querySelector('[data-bundle-share-modal]');
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.style.overflow = '';
  }

  setShareTab(tab) {
    this.shareTab = tab;
    document.querySelectorAll('[data-share-tab]').forEach((btn) => {
      btn.classList.toggle('on', btn.dataset.shareTab === tab);
    });

    const textarea = document.querySelector('[data-bundle-share-text]');
    if (textarea) textarea.value = this.buildShareText(tab);
  }

  copyShareField(selector) {
    const field = document.querySelector(selector);
    if (!field) return;

    const text = 'value' in field ? field.value : field.textContent;
    navigator.clipboard?.writeText(text).then(
      () => this.toast('Copied'),
      () => {
        field.select?.();
        document.execCommand?.('copy');
      }
    );
  }

  /**
   * Minimal toast for save/copy confirmations
   */
  toast(message) {
    let el = document.querySelector('[data-bundle-toast]');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-bundle-toast', '');
      el.className = 'bundle-toast';
      document.body.appendChild(el);
    }

    el.textContent = message;
    el.classList.add('bundle-toast--show');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => el.classList.remove('bundle-toast--show'), 2200);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.bundleBuilder = new BundleBuilder();
  });
} else {
  window.bundleBuilder = new BundleBuilder();
}
